import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { useRect } from '@reach/rect'
import classNames from 'classnames'

import styles from './styles.module.scss'

const ButtonGroup = ({
  className,
  children,
  activeIndex,
  defaultActiveIndex,
  onClick,
  groupLabel,
  role,
  isFullWidth,
  ...props
}) => {
  const [uncontrolledActiveIndex, setUncontrolledActiveIndex] = useState(
    defaultActiveIndex
  )
  const theActiveIndex = activeIndex ?? uncontrolledActiveIndex
  const refs = useRef(children.map(() => React.createRef()))
  // TODO: should refactor this so we aren't breaking rules of hooks
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const refSizes = refs.current.map((ref) => useRect(ref))

  const baseXPosition = refSizes[0] && refSizes[0].left

  if (!children) {
    return 'Please add <Button>components</Button> as children'
  }
  // TODO: Look at RadioGroup and refactor to look like it cuz this is total garbage - Brent
  const isActiveIndex = (passedIndex) =>
    activeIndex || activeIndex === 0
      ? passedIndex === activeIndex
      : passedIndex === uncontrolledActiveIndex
  const renderChildren = () =>
    // Extend Button components to automatically add styles and handle
    // onClick events
    React.Children.map(children, (child, index) => {
      if (React.isValidElement(child) && child.type.displayName === 'Button') {
        return React.cloneElement(child, {
          isActive: isActiveIndex(index),
          className: classNames(child.props.className, {
            [styles.isActive]: isActiveIndex(index),
          }),
          ref: refs.current[index],
          role: role === 'tablist' ? 'tab' : undefined,
          onClick: (event) => {
            if (!activeIndex && activeIndex !== 0)
              setUncontrolledActiveIndex(index)
            if (child.props.onClick) {
              child.props.onClick(event, index)
            } else {
              onClick(event, index)
            }
          },
        })
      }
      if (child) {
        return 'You can only pass <Button>components</Button>'
      }
      return null
    })

  const activeWidth = `${
    (refSizes[theActiveIndex] && refSizes[theActiveIndex].width) || 0
  }px`
  const transformX = `translateX(${
    (refSizes[theActiveIndex] &&
      Math.abs(refSizes[theActiveIndex].left - baseXPosition)) ||
    0
  }px)`

  return (
    <div
      {...props}
      className={classNames(className, styles.container, {
        [styles.isFullWidth]: isFullWidth,
      })}
      role={role}
      aria-label={groupLabel}
    >
      {renderChildren()}
      <div
        className={styles.activeBg}
        style={{
          width: activeWidth,
          transform: transformX,
        }}
      />
    </div>
  )
}

ButtonGroup.defaultProps = {
  role: 'group',
  size: 'medium',
  defaultActiveIndex: 0,
  activeIndex: null,
  isFullWidth: false,
  onClick: () => {},
}

ButtonGroup.propTypes = {
  /**
   * An ARIA role describing the button group.
   *   * @type {'group' | 'toolbar' | 'tablist'}
   */
  role: PropTypes.string,
  /**
   * Makes the button group take up the full width of it's parent
   */
  isFullWidth: PropTypes.bool,
  /**
   * Group Label used for assistive technologies
   */
  groupLabel: PropTypes.string.isRequired,
  /**
   * Sets the size for all Buttons in the group.
   *
   * @type ('small'|'medium'|'large')
   */
  size: PropTypes.string,
  /**
   * Sets which button is active on load
   * If you want to have none selected at start, pass null
   */
  // eslint-disable-next-line consistent-return
  defaultActiveIndex(props, propName) {
    if (props[propName] < 0) {
      return new Error(
        `defaultActiveIndex must be a number greater than 0. You passed ${props[propName]}`
      )
    }
  },
  /**
   * Controlled way to change which button is active
   * If you want to have none selected at start, pass null
   */
  activeIndex: PropTypes.number,
  /**
   * onClick function to apply to all buttons
   */
  onClick: PropTypes.func,
}

ButtonGroup.displayName = 'ButtonGroup'

export default ButtonGroup
